import { Abi, Chain, erc20Abi } from "viem";

// etherem abis
import elMerkleDistributorAbi from "@abi/ethereum/ELMerkleDistributor.json";
import kernelAirdrop_S2V2_MerkleDistributorAbi from "@abi/ethereum/KernelAirdrop_S2V2_MerkleDistributor_Eth.json";
import kernelAirdrop_Oct_MerkleDistributorAbi from "@abi/ethereum/KernelAirdrop_Oct.json";
import kernelAirdrop_Sep_MerkleDistributorAbi from "@abi/ethereum/KernelAirdrop_Sep.json";
import kernelAirdrop_S3V1_MerkleDistributorAbiEth from "@abi/ethereum/KernelAirdrop_S3V1_MerkleDistributor_Eth.json";
import kernelAirdrop_S3V1_MerkleDistributorAbiBsc from "@abi/bsc/KernelAirdrop_S3V1_MerkleDistributor_Bsc.json";
import elTokenABI from "@abi/ethereum/ELToken.json";
import kepAbi from "@abi/ethereum/KEP.json";
import lrtDepositPoolAbi from "@abi/ethereum/LRTDepositPool.json";
import lrtDepositPoolAbiInk from "@abi/ink/DepositPool.json";
import unstakingVaultAbi from "@abi/ethereum/LRTUnstakingVault.json";
import withdrawalManagerAbi from "@abi/ethereum/LRTWithdrawalManager.json";
import merkleDistributorAbi from "@abi/ethereum/MerkleDistributor.json";
import ScrollDistributorAbi from "@abi/scroll/ScrollMerkleDistributor.json";
import lrtDepositPoolAbiXL from "@abi/xlayer/LRTDepositPool.json";
import ethVaultJson from "@abi/vault/eth.json";
import gainVaultJson from "@abi/vault/gain.json";
import otherVaultJson from "@abi/vault/other.json";
import stableVaultJson from "@abi/vault/stable.json";
import lrtDepositPoolTacAbi from "@abi/tac/LRTDepositPoolTac.json";
import cryptaniumVaultJson from "@abi/vault/cryptanium.json";
// vault adapter abis
import GainAdapterETH from "@abi/vault/GainAdapterETH.json";
import rsETHAdapterJson from "@abi/vault/rsETHAdapter.json";

// arbitrum abis
import lrtDepositPoolAbiArb from "@abi/arbitrum/LRTDepositPool.json";

// common abi for blast scroll optimism
import lrtDepositPoolAbiCommon from "@abi/common/LRTDepositPool.json";

// manta abis
import tokenWrapperAbi from "@abi/common/wrseth.json";

// avalanche abis
import proxyAdminAbi from "@abi/avalanche/ProxyAdmin.json";
import proxyFactoryAbi from "@abi/avalanche/ProxyFactory.json";
import rsEthRateReceiverAbi from "@abi/avalanche/RsEthRateReceiver.json";
import wethOracleAbi from "@abi/avalanche/WethOracle.json";

// upgraded rsETH abi
import rsETHAbi from "@abi/ethereum/rsETH.json";

import { Hash } from "viem";
import {
  arbitrum,
  avalanche,
  base,
  blast,
  bsc,
  linea,
  mainnet,
  manta,
  mantle,
  mode,
  optimism,
  scroll,
  sonic,
  tac,
  unichain,
  xLayer,
  zircuit,
  zkSync,
  plasma,
  ink,
} from "viem/chains";
import { stable } from "./custom-chains";

export const XPL = "XPL";
export const ETHX = "ETHx";
export const STETH = "stETH";
export const SFRXETH = "sfrxETH";
export const KERNEL = "KERNEL";
export const ETH = "ETH";
export const BNB = "BNB";
export const OKB = "OKB";
export const WETH = "wETH";
export const WETHE = "wETH.e";
export const WSTETH = "wstETH";
export const RSETH = "rsETH";
export const WRSETH = "wrsETH";
export const WAGETH = "agETH";
export const AGETH = "agETH.axl";
export const HGETH = "hgETH";
export const TAC = "TAC";
export const AVAX = "AVAX";
export const SONIC = "SONIC";
export const USDT = "USDT";
export const USDC = "USDC";
export const SBUSD = "sbUSD";
export const GUSDT = "GUSDT";
export const MNT = "MNT";

export const VAULT_ALL_TOKENS = {
  eETH: "eETH",
  mETH: "mETH",
  rETH: "rETH",
  stETH: "stETH",
  swETH: "swETH",
  weETH: "weETH",
  wstETH: "wstETH",
  CUSD: "CUSD",
  USDT: "USDT",
  USDC: "USDC",
  xgUSD: "xgUSD",
};

export const enum TAGS {
  KELP_MILES = "kelpMiles",
  EL_POINTS = "elPoints",
  EXTRA_EL_POINTS = "extraELPoints",
}

export const LST_TAGS = {
  [TAGS.KELP_MILES]: {
    title: "Kernel Points",
    style: {
      backgroundColor: "#5D8A8A",
    },
  },
  [TAGS.EL_POINTS]: {
    title: "EL Points",
    style: {
      backgroundColor: "#78A5CF",
    },
  },
  [TAGS.EXTRA_EL_POINTS]: {
    title: "+25 EL Points/rsETH",
    style: {
      backgroundColor: "#DF60AC",
    },
  },
};

export type TokenDetails = {
  [x: string]: {
    symbol: string;
    decimals: number;
    address: Hash;
    abi: Abi;
    priority?: number;
    unstakePriority?: number;
    tags: Array<TAGS> | [];
    isLiquidStakeSupported?: boolean;
  };
};

export type ContractDetails = {
  [x: string]: {
    address: Hash;
    abi: Abi;
  };
};

export type ContractsConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPools: ContractDetails;
    tokens: TokenDetails;
    nativeToken: {
      symbol: string;
      decimals: number;
      address: string;
      tags: Array<TAGS> | [];
      isRestakeSupported: boolean;
      unstakePriority?: number;
      isVaultSupported?: boolean;
      isYieldVaultSupported?: boolean;
      isVaultWithdrawSupported?: boolean;
    };
    xtoken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
  };
};

export const ledgerSupportedChains = [mainnet, arbitrum, optimism, avalanche];

export const contractConfig: ContractsConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
      unstakePriority: 0,
      isVaultSupported: true,
      isYieldVaultSupported: true,
      isVaultWithdrawSupported: true,
    },
    xtoken: {
      address: "0xA1290d69c65A6Fe4DF752f95823fae25cB99e5A7",
      abi: rsETHAbi as Abi,
      symbol: "rsETH",
      decimals: 18,
    },

    tokens: {
      [STETH]: {
        address: "0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84",
        abi: erc20Abi,
        symbol: "stETH",
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        unstakePriority: -1,
      },
      [ETHX]: {
        address: "0xA35b1B31Ce002FBF2058D22F30f95D405200A15b",
        abi: erc20Abi,
        symbol: "ETHx",
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        isLiquidStakeSupported: true,
      },
      [SFRXETH]: {
        address: "0xac3e018457b222d93114458476f3e3416abbe38f",
        abi: erc20Abi,
        symbol: "sfrxETH",
        decimals: 18,
        tags: [],
        unstakePriority: -2,
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x036676389e48133b63a802f8635ad39e752d375d",
        abi: lrtDepositPoolAbi as Abi,
      },
      kep: {
        address: "0x8E3A59427B1D87Db234Dd4ff63B25E4BF94672f4",
        abi: kepAbi as Abi,
      },
      merkleDistributor: {
        address: "0x2DDB11443bD9Ceb92d4951A05f55eb7096EB53d3",
        abi: merkleDistributorAbi as Abi,
      },
      elToken: {
        address: "0xec53bF9167f50cDEB3Ae105f56099aaaB9061F83",
        abi: elTokenABI as Abi,
      },
      elMerkleDistributor: {
        address: "0xc135b516e399C1ed702588D887FBBE6F2d1bA27A",
        abi: elMerkleDistributorAbi as Abi,
      },
      elPkMerkleDistributor: {
        address: "0x9bB6d4b928645EdA8f9C019495695BA98969eFF1",
        abi: elMerkleDistributorAbi as Abi,
      },
      kernelAirdrop_S2V2_MerkleDistributor: {
        address: "0x2B1B971dFD6b23a1E80fe95FDD39a33Cd06ac450",
        abi: kernelAirdrop_S2V2_MerkleDistributorAbi as Abi,
      },
      kernelAirdrop_SEP_MerkleDistributor: {
        address: "0xD0d483B612634741dd3eDDe4D60E87AaBB946231",
        abi: kernelAirdrop_Sep_MerkleDistributorAbi as Abi,
      },
      kernelAirdrop_OCT_MerkleDistributor: {
        address: "0xbaC4957Bb140c3b19E5c1151203e515a799a8Dfb",
        abi: kernelAirdrop_Oct_MerkleDistributorAbi as Abi,
      },
      kernelAirdrop_S3V1_MerkleDistributorEth: {
        address: "0xA182277f7E71e6cEc28d8F4dc005727CDd9c0E84",
        abi: kernelAirdrop_S3V1_MerkleDistributorAbiEth as Abi,
      },
      lrtWithdrawalManager: {
        address: "0x62De59c08eB5dAE4b7E6F7a8cAd3006d6965ec16",
        abi: withdrawalManagerAbi as Abi,
      },
      lrtUnstakingVault: {
        address: "0xc66830E2667bc740c0BED9A71F18B14B8c8184bA",
        abi: unstakingVaultAbi as Abi,
      },
    },
  },
  [arbitrum.id]: {
    chain: arbitrum,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
      isVaultSupported: true,
    },
    xtoken: {
      address: "0x4186bfc76e2e237523cbc30fd220fe055156b41f",
      abi: erc20Abi,
      symbol: "rsETH",
      decimals: 18,
    },

    tokens: {
      // [WSTETH]: {
      //   address: "0x5979D7b546E38E414F7E9822514be443A4800529",
      //   abi: erc20Abi,
      //   symbol: WSTETH,
      //   decimals: 18,
      //   tags: [TAGS.KELP_MILES],
      //   unstakePriority: -1,
      // },
      [ETHX]: {
        address: "0xED65C5085a18Fa160Af0313E60dcc7905E944Dc7",
        abi: erc20Abi,
        symbol: ETHX,
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        isLiquidStakeSupported: true,
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x376A7564AF88242D6B8598A5cfdD2E9759711B61",
        abi: lrtDepositPoolAbiArb as Abi,
      },
    },
  },
  [mode.id]: {
    chain: mode,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xbDf612E616432AA8e8D7d8cC1A9c934025371c5C",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [blast.id]: {
    chain: blast,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0x1558959f1a032F83f24A14Ff539944A926C51bdf",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [scroll.id]: {
    chain: scroll,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
      isVaultSupported: false,
    },
    xtoken: {
      address: "0xa25b25548B4C98B0c7d3d27dcA5D5ca743d68b7F",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xb80deaecd7F4Bca934DE201B11a8711644156a0a",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
      scrollDistributor: {
        address: "0xbE7E2d809E2C7405B5972292986324a798921D98",
        abi: ScrollDistributorAbi as Abi,
      },
    },
  },
  [optimism.id]: {
    chain: optimism,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0x87eEE96D50Fb761AD85B1c982d28A042169d61b1",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xaAA687e218F9B53183A6AA9639FBD9D6e69EcB73",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [ink.id]: {
    chain: ink,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0x9f0a74A92287E323Eb95c1cd9eCdBEb0e397cAe4",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xcD464f47Cb8AEd70F7e85dd5eca20Db021B5246B",
        abi: lrtDepositPoolAbiInk as Abi,
      },
    },
  },
  [base.id]: {
    chain: base,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0xEDfa23602D0EC14714057867A78d01e94176BEA0",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {
      [WSTETH]: {
        address: "0xc1CBa3fCea344f92D9239c08C0568f6F2F0ee452",
        abi: erc20Abi,
        symbol: WSTETH,
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        unstakePriority: -1,
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x291088312150482826b3A37d5A69a4c54DAa9118",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [linea.id]: {
    chain: linea,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
      isVaultSupported: true,
    },
    xtoken: {
      address: "0xD2671165570f41BBB3B0097893300b6EB6101E6C",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0x057297e44a3364139edcf3e1594d6917ed7688c2",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [zkSync.id]: {
    chain: zkSync,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0xd4169E045bcF9a86cC00101225d9ED61D2F51af2",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0x41b300f5A619973b20931f0944C85DB229d5E27f",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [xLayer.id]: {
    chain: xLayer,
    nativeToken: {
      symbol: OKB,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0x5A71f5888EE05B36Ded9149e6D32eE93812EE5e9",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETH]: {
        address: "0x5A77f1443D16ee5761d310e38b62f77f726bC71c",
        abi: erc20Abi,
        symbol: WETH,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x4Ef626efE4a3A279a9DC7e7a91C1c9CaaAE8e159",
        abi: lrtDepositPoolAbiXL as Abi,
      },
    },
  },
  [zircuit.id]: {
    chain: zircuit,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0x311a51Ff8839B6afcAA9426BdBffDF2e70A0dA25",
      abi: erc20Abi,
      symbol: WRSETH,
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xca276450b2c26061785CF11668dd481168E102Cb",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [bsc.id]: {
    chain: bsc,
    nativeToken: {
      symbol: BNB,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
      unstakePriority: 0,
      isVaultSupported: false,
      isYieldVaultSupported: false,
    },
    xtoken: {
      address: "0x0000000000000000000000000000000000000000", // Placeholder, replace with actual address if available
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      kernelAirdrop_S3V1_MerkleDistributorBsc: {
        address: "0xc405487eb3a42651E174586f7F38856cC19E9976",
        abi: kernelAirdrop_S3V1_MerkleDistributorAbiBsc as Abi,
      },
    },
  },
  [unichain.id]: {
    chain: unichain,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0xc3eACf0612346366Db554C991D7858716db09f58",
      abi: erc20Abi,
      symbol: "rsETH",
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xeCB98C5A390EFE3a3ad02b804e5c3E568e9D5f0f",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [tac.id]: {
    chain: tac,
    nativeToken: {
      symbol: TAC,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0x5448BBf60Ee2edBCd32F032f3294982f4ad1119e",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETH]: {
        address: "0x61D66bC21fED820938021B06e9b2291f3FB91945",
        abi: erc20Abi,
        symbol: WETH,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x454CB45b309ee9798728168bA0244B496C1F98b8",
        abi: lrtDepositPoolTacAbi as Abi,
      },
    },
  },
  [avalanche.id]: {
    chain: avalanche,
    nativeToken: {
      symbol: WETHE,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0x7bFd4CA2a6Cf3A3fDDd645D10B323031afe47FF0",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETHE]: {
        address: "0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB",
        abi: erc20Abi,
        symbol: WETHE,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x72FB3F4F0B3cD77eF7556fa4960bF4aEBAA47009",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
      proxyFactory: {
        address: "0x4Ff0b2CaeFeed2906e96931AD74e265EE2abB61f",
        abi: proxyFactoryAbi as Abi,
      },
      proxyAdmin: {
        address: "0x9A7fA6fE70F2A23dc3980DF69f922b6961FbbE81",
        abi: proxyAdminAbi as Abi,
      },
      rsETHRateReceiver: {
        address: "0x2A2F37D29143AEa599c57169817A48c04664150b",
        abi: rsEthRateReceiverAbi as Abi,
      },
      wethOracle: {
        address: "0x5663ea61Dd44986bB92fCA764d1bE02bde08399A",
        abi: wethOracleAbi as Abi,
      },
    },
  },
  [sonic.id]: {
    chain: sonic,
    nativeToken: {
      symbol: WETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0x7c050Be1Dded733BD44116b60A8a35125ba47459",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETH]: {
        address: "0x50c42dEAcD8Fc9773493ED674b675bE577f2634b",
        abi: erc20Abi,
        symbol: WETH,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x6189918EF83A73a9CD97BBff0d91af6ADCc9841D",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [plasma.id]: {
    chain: plasma,
    nativeToken: {
      symbol: XPL,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0xe561FE05C39075312Aa9Bc6af79DdaE981461359",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETH]: {
        address: "0x9895D81bB462A195b4922ED7De0e3ACD007c32CB",
        abi: erc20Abi,
        symbol: WETH,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0xd59d17b0503b45F365670bDe9D002B9886EBC02b",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [stable.id]: {
    chain: stable,
    nativeToken: {
      symbol: GUSDT,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0xFB0946be33F0C86Da798ab552e6A36fB80265E2a",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETH]: {
        address: "0x783129E4d7bA0Af0C896c239E57C06DF379aAE8c",
        abi: erc20Abi,
        symbol: WETH,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0xeB885Bf774c7cab68A5B0a0cCE0789800ff3923B",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [mantle.id]: {
    chain: mantle,
    nativeToken: {
      symbol: MNT,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0x93e855643e940D025bE2e529272e4Dbd15a2Cf74",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETH]: {
        address: "0xdEAddEaDdeadDEadDEADDEAddEADDEAddead1111",
        abi: erc20Abi,
        symbol: WETH,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0xb6EF312b80fAcBbECa42dDF8b47934a762557048",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
};

type SwapTokenDetails = {
  symbol: string;
  decimals: number;
  address: Hash;
  abi: Abi;
};

export type SwapContractsConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    tokens: {
      rsETH: SwapTokenDetails;
      wrsETH: SwapTokenDetails;
    };
  };
};

export type VaultSwapContractsConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    tokens: {
      agETH: SwapTokenDetails;
      wagETH: SwapTokenDetails;
    };
  };
};

export type VaultConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPool: { address: Hash; abi: Abi };
    rsETHAdapterPool?: { address: Hash; abi: Abi };
    xToken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
  };
};
export type StableVaultConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPool: { address: Hash; abi: Abi };
    xToken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
    tokens: {
      [x: string]: {
        symbol: string;
        decimals: number;
        address: Hash;
        abi: Abi;
        priority?: number;
        isLiquidStakeSupported?: boolean;
        tags?: Array<TAGS> | [];
      };
    };
  };
};
export type YieldVaultConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPool: { address: Hash; abi: Abi };
    gainAdapterETH: { address: Hash; abi: Abi };
    xToken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
    tokens: {
      [x: string]: {
        symbol: string;
        decimals: number;
        address: Hash;
        abi: Abi;
        priority?: number;
        isLiquidStakeSupported?: boolean;
        tags?: Array<TAGS> | [];
      };
    };
  };
};

export type CryptaniumVaultConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPool: { address: Hash; abi: Abi };
    xToken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
    tokens: {
      [x: string]: {
        symbol: string;
        decimals: number;
        address: Hash;
        abi: Abi;
        priority?: number;
        isLiquidStakeSupported?: boolean;
        tags?: Array<TAGS> | [];
      };
    };
  };
};

export const vaultsContractConfig: VaultConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    interactionPool: {
      address: "0xe1B4d34E8754600962Cd944B535180Bd758E6c2e",
      abi: ethVaultJson as Abi,
    },
    rsETHAdapterPool: {
      address: "0xbf28c9fcb12a97441488f9c68faa49811a98688a",
      abi: rsETHAdapterJson as Abi,
    },
    xToken: {
      address: "0xe1B4d34E8754600962Cd944B535180Bd758E6c2e",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
  [arbitrum.id]: {
    chain: arbitrum,
    interactionPool: {
      address: "0x77F5979D8eA6d72d6C6C451eC23c772D68211c5f",
      abi: otherVaultJson as Abi,
    },
    xToken: {
      address: "0xF1A88250532a4A66A2420a8cbB434Da82E1E2cA1",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
  [scroll.id]: {
    chain: scroll,
    interactionPool: {
      address: "0x6c5513F8701a6E58C82D9a0585A2E533A7fC773b",
      abi: otherVaultJson as Abi,
    },
    xToken: {
      address: "0xd44605d3E5eF9A73379Ce5258B06e4383c6FF32a",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
  [linea.id]: {
    chain: linea,
    interactionPool: {
      address: "0x7F260B785E3B74155a39d82251B47D05ae0d6c61",
      abi: otherVaultJson as Abi,
    },
    xToken: {
      address: "0x2a4f1dcc79b83608f9e3BC1F3F55fBEfCBFaE885",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
};

export const yieldVaultContractConfig: YieldVaultConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    interactionPool: {
      address: "0xc824A08dB624942c5E5F330d56530cD1598859fD",
      abi: gainVaultJson as Abi,
    },
    gainAdapterETH: {
      address: "0xB185D98056419029daE7120EcBeFa0DbC12c283A",
      abi: GainAdapterETH as Abi,
    },
    xToken: {
      address: "0xc824A08dB624942c5E5F330d56530cD1598859fD",
      abi: erc20Abi,
      symbol: "hgETH",
      decimals: 18,
    },
    tokens: {
      [VAULT_ALL_TOKENS.eETH]: {
        address: "0x35fA164735182de50811E8e2E824cFb9B6118ac2",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.eETH,
        decimals: 18,
      },
      [VAULT_ALL_TOKENS.rETH]: {
        address: "0xae78736Cd615f374D3085123A210448E74Fc6393",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.rETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.stETH]: {
        address: "0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.stETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.swETH]: {
        address: "0xf951E335afb289353dc249e82926178EaC7DEd78",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.swETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.weETH]: {
        address: "0xCd5fE23C85820F7B72D0926FC9b05b43E359b7ee",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.weETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.wstETH]: {
        address: "0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.wstETH,
        decimals: 18,
        priority: -1,
      },
    },
  },
};

export const stableVaultContractConfig: StableVaultConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    interactionPool: {
      address: "0x8ecc0b419dfe3ae197bc96f2a03636b5e1be91db",
      abi: stableVaultJson as Abi,
    },
    xToken: {
      address: "0x8ecc0b419dfe3ae197bc96f2a03636b5e1be91db",
      abi: erc20Abi,
      symbol: SBUSD,
      decimals: 6,
    },
    tokens: {
      [USDC]: {
        address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
        abi: erc20Abi,
        symbol: USDC,
        decimals: 6,
        priority: -1,
      },
      [USDT]: {
        address: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
        abi: erc20Abi,
        symbol: USDT,
        decimals: 6,
      },
    },
  },
};

export const cryptaniumVaultContractConfig: CryptaniumVaultConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    interactionPool: {
      address: "0x6cebdb128d4d5895b8ed7395266fa88406294b69",
      abi: cryptaniumVaultJson as Abi,
    },
    xToken: {
      address: "0x6cebdb128d4d5895b8ed7395266fa88406294b69",
      abi: erc20Abi,
      symbol: VAULT_ALL_TOKENS.xgUSD,
      decimals: 6,
    },
    tokens: {
      [VAULT_ALL_TOKENS.USDT]: {
        address: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.USDT,
        decimals: 6,
        priority: -1,
      },
      // [VAULT_ALL_TOKENS.USDC]: {
      //   address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
      //   abi: erc20Abi,
      //   symbol: VAULT_ALL_TOKENS.CUSD,
      //   decimals: 6,
      //   priority: -1,
      // },
    },
  },
};

export const vaultSwapContractsConfig: VaultSwapContractsConfigMap = {
  [arbitrum.id]: {
    chain: arbitrum,
    tokens: {
      agETH: {
        address: "0x1bD0Fe8E92a157D3ef66C9FB9e38621252b407c2",
        abi: erc20Abi,
        symbol: "agETH",
        decimals: 18,
      },
      wagETH: {
        address: "0xF1A88250532a4A66A2420a8cbB434Da82E1E2cA1",
        abi: tokenWrapperAbi as Abi,
        symbol: "wagETH",
        decimals: 18,
      },
    },
  },
  [scroll.id]: {
    chain: scroll,
    tokens: {
      agETH: {
        address: "0x1bD0Fe8E92a157D3ef66C9FB9e38621252b407c2",
        abi: erc20Abi,
        symbol: "agETH",
        decimals: 18,
      },
      wagETH: {
        address: "0xd44605d3E5eF9A73379Ce5258B06e4383c6FF32a",
        abi: tokenWrapperAbi as Abi,
        symbol: "wagETH",
        decimals: 18,
      },
    },
  },
  [linea.id]: {
    chain: linea,
    tokens: {
      agETH: {
        address: "0x1bD0Fe8E92a157D3ef66C9FB9e38621252b407c2",
        abi: erc20Abi,
        symbol: "agETH",
        decimals: 18,
      },
      wagETH: {
        address: "0x2a4f1dcc79b83608f9e3BC1F3F55fBEfCBFaE885",
        abi: tokenWrapperAbi as Abi,
        symbol: "wagETH",
        decimals: 18,
      },
    },
  },
};

export const swappingContractsConfig: SwapContractsConfigMap = {
  [manta.id]: {
    chain: manta,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x9dd4f9EeE9B05D1ebec1d4aAE7Ae9F5d8D235CD4",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [mode.id]: {
    chain: mode,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [blast.id]: {
    chain: blast,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [scroll.id]: {
    chain: scroll,
    tokens: {
      rsETH: {
        address: "0x65421ba909200b81640d98B979d07487C9781B66",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xa25b25548B4C98B0c7d3d27dcA5D5ca743d68b7F",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [optimism.id]: {
    chain: optimism,
    tokens: {
      rsETH: {
        address: "0x043849686EE254ada46A432770E1a491491FC44D",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x87eEE96D50Fb761AD85B1c982d28A042169d61b1",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [base.id]: {
    chain: base,
    tokens: {
      rsETH: {
        address: "0x1Bc71130A0e39942a7658878169764Bbd8A45993",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xEDfa23602D0EC14714057867A78d01e94176BEA0",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [linea.id]: {
    chain: linea,
    tokens: {
      rsETH: {
        address: "0xb999Ea589E0a1Cce9153601daC2D6e203c2fD577",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xD2671165570f41BBB3B0097893300b6EB6101E6C",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [zkSync.id]: {
    chain: zkSync,
    tokens: {
      rsETH: {
        address: "0x6bE2425C381eb034045b527780D2Bf4E21AB7236",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xd4169E045bcF9a86cC00101225d9ED61D2F51af2",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [xLayer.id]: {
    chain: xLayer,
    tokens: {
      rsETH: {
        address: "0x1B3a9A689Ba7555F9D7984D7Ad4025574Ed5A0f9",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x5A71f5888EE05B36Ded9149e6D32eE93812EE5e9",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [zircuit.id]: {
    chain: zircuit,
    tokens: {
      rsETH: {
        address: "0x571405D597091e8728d8240F558BAc01275E8659",
        abi: erc20Abi,
        symbol: RSETH,
        decimals: 18,
      },
      wrsETH: {
        address: "0x311a51Ff8839B6afcAA9426BdBffDF2e70A0dA25",
        abi: tokenWrapperAbi as Abi,
        symbol: WRSETH,
        decimals: 18,
      },
    },
  },
  [tac.id]: {
    chain: tac,
    tokens: {
      rsETH: {
        address: "0x9eCaf80c1303CCA8791aFBc0AD405c8a35e8d9f1",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x5448BBf60Ee2edBCd32F032f3294982f4ad1119e",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [avalanche.id]: {
    chain: avalanche,
    tokens: {
      rsETH: {
        address: "0xc430c78Da6E4AF49bD115F0329D154Bb135f1363",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x7bFd4CA2a6Cf3A3fDDd645D10B323031afe47FF0",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [sonic.id]: {
    chain: sonic,
    tokens: {
      rsETH: {
        address: "0xd75787bA9ABa324420d522BdA84c08c87e5099b1",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x7c050Be1Dded733BD44116b60A8a35125ba47459",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [ink.id]: {
    chain: ink,
    tokens: {
      rsETH: {
        address: "0xc3eACf0612346366Db554C991D7858716db09f58",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x9f0a74A92287E323Eb95c1cd9eCdBEb0e397cAe4",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [plasma.id]: {
    chain: plasma,
    tokens: {
      rsETH: {
        address: "0x9eCaf80c1303CCA8791aFBc0AD405c8a35e8d9f1",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xe561FE05C39075312Aa9Bc6af79DdaE981461359",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [stable.id]: {
    chain: stable,
    tokens: {
      rsETH: {
        address: "0x9eCaf80c1303CCA8791aFBc0AD405c8a35e8d9f1",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xFB0946be33F0C86Da798ab552e6A36fB80265E2a",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [mantle.id]: {
    chain: mantle,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x93e855643e940D025bE2e529272e4Dbd15a2Cf74",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
};
